import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { fetchTestChannelRequests } from "../../api/client";
import { StatusIcon } from "../../lib/status";
import { getCleanSubject, getOriginalDate } from "../../helpers/emailMetadata";
import type { PaginatedResponse, ProcessingRequest } from "../../api/types";

/* ============================================================
   Elenco delle pratiche nate da una conversazione di PROVA
   (`channel='test'`), rotta `/settings/agente/pratiche`.

   PERCHE' QUESTA LISTA ESISTE
     `ConversazioniList.tsx` (voce «Agente», `/conversazioni`) e la coda RDI
     escludono SEMPRE `channel='test'`, per non gonfiare il lavoro vero con
     messaggi di prova — corretto. Ma il risultato, senza questa pagina, era
     che una pratica materializzata durante un test spariva: non appariva in
     nessuna lista, raggiungibile solo aprendo `/review3/:id` a mano se se ne
     conosceva l'id.

   Stessa `/api/v1/requests` di `fetchAgentRequests`, con `only_test_channel=true`
   al posto di `origin=conversation`: il backend, con questo flag, inverte
   l'esclusione e restituisce SOLO `channel='test'`. Click riga → `/review3/:id`,
   come `ConversazioniList.tsx`.

   Pattern tabella copiato da `ConversazioniList.tsx`/`RequestList.tsx`:
   `tableLayout: fixed` + `minWidth` NUMERICO letterale sul <table> (mai
   `var(--space-*)` come larghezza). */

const DEFAULT_PAGE_SIZE = 20;
const PAGE_SIZE_OPTIONS = [20, 50, 100];

const STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: "", label: "Tutti" },
  { value: "pending_review", label: "Da validare" },
  { value: "received", label: "In elaborazione" },
  { value: "done", label: "Approvate" },
  { value: "discarded", label: "Scartate" },
];

function fmt(iso: string | null): string {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return `${d.toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit" })} ${d.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}`;
  } catch { return iso; }
}

function fmtFull(iso: string | null): string {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("it-IT"); } catch { return iso; }
}

function contactLabel(item: ProcessingRequest): string {
  const sender = item.incoming_data?.sender;
  return sender?.name || sender?.phone || sender?.email || "—";
}

function objectLabel(item: ProcessingRequest): string {
  const edificio = item.matches?.[0]?.entity_matches?.edificio?.best?.entity;
  const descrizione = edificio?.fields?.descrizione;
  const indirizzo = edificio?.fields?.indirizzo;
  if (descrizione || indirizzo) {
    return [descrizione, indirizzo].filter(Boolean).join(" — ");
  }
  return getCleanSubject(item) || "—";
}

export function ProvePraticheList() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const status = searchParams.get("status") || "";
  const page = parseInt(searchParams.get("page") || "1", 10);
  const pageSize = parseInt(searchParams.get("size") || String(DEFAULT_PAGE_SIZE), 10);

  const [data, setData] = useState<PaginatedResponse<ProcessingRequest> | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  function setFilter(updates: Record<string, string | null>) {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      for (const [k, v] of Object.entries(updates)) {
        if (v === null || v === "") next.delete(k);
        else next.set(k, v);
      }
      if (!("page" in updates)) next.delete("page");
      return next;
    }, { replace: true });
  }

  const load = useCallback(() => {
    setIsLoading(true);
    fetchTestChannelRequests({
      status: status || undefined,
      page,
      size: pageSize,
    })
      .then((d) => { setData(d); setError(null); })
      .catch((e) => setError(e instanceof Error ? e.message : "Errore caricamento"))
      .finally(() => setIsLoading(false));
  }, [status, page, pageSize]);

  useEffect(() => { load(); }, [load]);

  const totalPages = data ? Math.ceil(data.total / pageSize) : 0;

  return (
    <div style={{ padding: "var(--space-4)" }}>
      <div className="flow-breadcrumb" style={{ marginBottom: "var(--space-2)" }}>
        <button className="btn-link" onClick={() => navigate("/settings/agente")}>
          <span className="material-symbols-outlined" style={{ fontSize: 18 }}>arrow_back</span>
          Agente Conversazionale
        </button>
      </div>

      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", marginBottom: "var(--space-3)", gap: "var(--space-3)" }}>
        <h1 className="page-title">Pratiche di prova</h1>
        <button className="btn btn-ghost btn-sm" onClick={load} title="Aggiorna">
          <span className="material-symbols-outlined" style={{ fontSize: "var(--font-size-md)" }}>refresh</span>
        </button>
      </div>

      <p className="text-sm text-muted" style={{ marginTop: 0, marginBottom: "var(--space-3)" }}>
        Pratiche materializzate da una conversazione sul canale di prova (<code>test</code>): non
        sono lavoro reale, per questo non compaiono ne' nella coda RDI ne' nella dashboard «Agente».
        Click su una riga per aprirla in revisione.
      </p>

      <div className="card" style={{ padding: "var(--space-3)", marginBottom: "var(--space-3)" }}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "var(--space-2)", alignItems: "center" }}>
          <span className="text-muted text-xs">Stato:</span>
          {STATUS_OPTIONS.map((opt) => (
            <button key={opt.value} className={`btn btn-sm ${status === opt.value ? "btn-primary" : "btn-outline"}`}
              onClick={() => setFilter({ status: opt.value || null })}>
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {isLoading && !data && <div className="loading-center"><div className="spinner spinner-lg" /></div>}

      {error && (
        <div className="card" style={{ background: "var(--indaco-danger-light)", color: "var(--indaco-danger)" }}>
          <span className="material-symbols-outlined" style={{ fontSize: "var(--font-size-md)", marginRight: "var(--space-1)" }}>error</span> Errore: {error}
        </div>
      )}

      {data && (
        <div className="card" style={{ padding: 0, opacity: isLoading ? 0.6 : 1, transition: "opacity 150ms" }}>
          <div className="table-container">
            <table style={{ tableLayout: "fixed", width: "100%", minWidth: 760 }}>
              <thead>
                <tr>
                  <th style={{ width: 40, textAlign: "center" }}></th>
                  <th style={{ width: 180 }}>Contatto</th>
                  <th>Oggetto / Edificio</th>
                  <th style={{ width: 100 }}>Creata</th>
                  <th style={{ width: 100 }}>Ultima attività</th>
                </tr>
              </thead>
              <tbody>
                {data.items.length === 0 && !isLoading && (
                  <tr>
                    <td colSpan={5} style={{ textAlign: "center", padding: "var(--space-10)", color: "var(--indaco-gray-400)" }}>
                      Nessuna pratica di prova
                    </td>
                  </tr>
                )}
                {data.items.map((item) => (
                  <tr key={item.id} onClick={() => navigate(`/review3/${item.id}`)} style={{ cursor: "pointer" }}>
                    <td style={{ textAlign: "center", overflow: "hidden" }} title={item.id}>
                      <StatusIcon status={item.status} />
                    </td>
                    <td className="text-sm" style={{ overflow: "hidden" }}>
                      <span className="truncate" style={{ display: "block" }}>{contactLabel(item)}</span>
                    </td>
                    <td style={{ overflow: "hidden" }}>
                      <span className="truncate" style={{ display: "block", fontSize: "var(--font-size-sm)" }} title={objectLabel(item)}>
                        {objectLabel(item)}
                      </span>
                    </td>
                    <td style={{ whiteSpace: "nowrap", overflow: "hidden" }} title={fmtFull(getOriginalDate(item))} className="text-sm">
                      {fmt(getOriginalDate(item))}
                    </td>
                    <td style={{ whiteSpace: "nowrap", overflow: "hidden" }} title={fmtFull(item.updated_at)} className="text-sm">
                      {fmt(item.updated_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {data && data.total > 0 && (
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", marginTop: "var(--space-3)", gap: "var(--space-2)" }}>
          {totalPages > 1 && (
            <div className="pagination">
              <button disabled={page <= 1} onClick={() => setFilter({ page: page === 2 ? null : String(page - 1) })}>
                <span className="material-symbols-outlined" style={{ fontSize: "var(--font-size-md)" }}>chevron_left</span>
              </button>
              <span className="text-sm" style={{ padding: "0 var(--space-2)" }}>{page} / {totalPages}</span>
              <button disabled={page >= totalPages} onClick={() => setFilter({ page: String(page + 1) })}>
                <span className="material-symbols-outlined" style={{ fontSize: "var(--font-size-md)" }}>chevron_right</span>
              </button>
            </div>
          )}
          <div style={{ display: "flex", alignItems: "center", gap: "var(--space-2)", fontSize: "var(--font-size-sm)" }}>
            <span className="text-muted">Mostra</span>
            <select className="form-input" value={pageSize}
              style={{ width: 72, padding: "var(--space-1) var(--space-2)", fontSize: "var(--font-size-sm)" }}
              onChange={(e) => setFilter({ size: e.target.value === String(DEFAULT_PAGE_SIZE) ? null : e.target.value, page: null })}>
              {PAGE_SIZE_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <span className="text-muted">di {data.total}</span>
          </div>
        </div>
      )}
    </div>
  );
}
